import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon } from "@sagot/ui";
import { createContext, useContext } from "react";
import { useFormatters, type BillingOverview } from "../shared";

/** True while the workspace is billed by invoice (`overview.billing_mode === "manual"`, DEC-155). */
const ManualBillingContext = createContext(false);
export const ManualBillingProvider = ManualBillingContext.Provider;

/** Billing actions read this to say "billed by invoice" instead of "no permission". */
export function useIsManualBilling(): boolean {
  return useContext(ManualBillingContext);
}

export function isManualBilling(overview: Pick<BillingOverview, "billing_mode">): boolean {
  return overview.billing_mode === "manual";
}

interface ManualBillingNoticeProps {
  overview: Pick<BillingOverview, "manual_billing" | "plan">;
  onContactSales: () => void;
}

/**
 * "Billed by invoice · managed by Sagot": shown at the top of Billing for an invoiced workspace.
 * The term and agreed price come from the server; the staff note never reaches the console.
 */
export function ManualBillingNotice({ overview, onContactSales }: ManualBillingNoticeProps) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const terms = overview.manual_billing;
  return (
    <section className="card border-primary" aria-labelledby="manual-billing-title" data-testid="manual-billing-notice">
      <div className="card-body flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <span className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary-ink" aria-hidden="true">
            <Icon name="file-text" className="size-5" />
          </span>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="manual-billing-title" className="text-base font-extrabold tracking-tight">
                {t("billing.manual.title")}
              </h2>
              <Badge tone="brand">{t("billing.manual.badge")}</Badge>
            </div>
            <p className="mt-1 max-w-2xl text-sm text-fg-muted">{t("billing.manual.body")}</p>
            <ul className="mt-3 space-y-1 text-sm">
              {terms?.starts_on && terms.ends_on ? <li>{t("billing.manual.term", { start: fmt.date(terms.starts_on), end: fmt.date(terms.ends_on) })}</li> : null}
              {terms?.agreed_price ? <li>{t("billing.manual.agreed", { price: fmt.money(terms.agreed_price, terms.currency) })}</li> : null}
            </ul>
          </div>
        </div>
        <Button variant="secondary" onClick={onContactSales}>
          <Icon name="mail" />
          {t("billing.manual.contact")}
        </Button>
      </div>
    </section>
  );
}
